import { atLeast } from './access-level.enums.js';
import { isPermissionKey, requiredLevel } from './permissions.catalog.js';
/**
 * Which layer of the model answered — the rungs of the capability ladder, named.
 *
 * `GRANTED` covers both «a role gives it» and «a personal ALLOW gives it», because the folded view
 * above cannot tell them apart and **no decision may**: the two are the same answer. Splitting them
 * for a screen is `capabilitySource` below, and it is reporting, not deciding.
 */
export const CAPABILITY_OUTCOMES = [
    'OWNER',
    'DENIED_BY_OVERRIDE',
    'GRANTED',
    'NOT_GRANTED',
];
/**
 * Layers 1–3: "is this subject allowed to do this at all, anywhere in the organization" — and by
 * which rung.
 *
 * **This is the only capability ladder in the product.** Everything that needs the answer derives
 * it from here: the boolean below, the `Decision` with a `DenyReason` in
 * `server/src/domain/access/authorize.util.ts`, and the per-permission origin the administration
 * screen renders. A second ladder written for any one of them would agree on the day it was written
 * and disagree on the day one of them was edited — and the screen that explains an administrator's
 * own permission model would then be wrong exactly when it is consulted.
 *
 * The order of the branches is the order of the model, and each one is load-bearing:
 *
 * 1. ownership first — §5 forbids writing a DENY on the owner, and a row that got there anyway (an
 *    older release, a restore, a bug) must not be able to lock an organization out of itself;
 * 2. a DENY override next: it beats every ALLOW below it;
 * 3. roles and ALLOW overrides grant; anything else refuses. Fail-closed by default.
 */
export const capabilityOutcome = (view, key) => {
    if (view.isOwner)
        return 'OWNER';
    if (view.denied.has(key))
        return 'DENIED_BY_OVERRIDE';
    return view.permissions.has(key) ? 'GRANTED' : 'NOT_GRANTED';
};
/** Which rungs mean «yes». The one place the allow/deny line is drawn. */
export const outcomeAllows = (outcome) => outcome === 'OWNER' || outcome === 'GRANTED';
/** Layers 1–3 as the plain boolean most callers want. Derived, never restated. */
export const effectivePermission = (view, key) => outcomeAllows(capabilityOutcome(view, key));
/**
 * The same rungs, with the granted one split by where the grant came from.
 *
 * What the administration screen of EPIC-015 shows next to every permission: inherited from a role,
 * widened by a personal exception, taken away by one, or never granted. `OWNER` is its own answer
 * rather than «granted by everything», because ownership is not a grant anybody made and cannot be
 * taken back by editing one.
 */
export const CAPABILITY_SOURCES = [
    'OWNER',
    'OVERRIDE_DENY',
    'OVERRIDE_ALLOW',
    'ROLE',
    'NOT_GRANTED',
];
export const sourceAllows = (source) => source === 'OWNER' || source === 'OVERRIDE_ALLOW' || source === 'ROLE';
/**
 * Where the capability answer came from — reporting on the ladder, not a second walk down it.
 *
 * `allowedByOverride` is the subset of `view.permissions` that arrived through an ALLOW exception
 * rather than through a role; it is the one fact the folded view drops, and the caller that wants
 * an origin is the caller that already read the exception rows. It is required rather than
 * defaulted, because a default would answer `ROLE` for a grant that came from an exception — an
 * unobservable lie today and a wrong screen the first time somebody reads the field.
 *
 * **Attribution never moves an answer across the allow/deny line.** `OVERRIDE_ALLOW` and `ROLE` are
 * both allowing, and the outcome they refine is the same `GRANTED`; the property is asserted over
 * every shape of view in `packages/shared/test/permissions/capability-source.test.ts` — the path is
 * spelled from the repository root because `test/permissions/` also exists there and holds
 * different files. That is what makes it safe for the screen and the guard to share one ladder.
 */
export const capabilitySource = (view, key, allowedByOverride) => {
    const outcome = capabilityOutcome(view, key);
    if (outcome === 'OWNER')
        return 'OWNER';
    if (outcome === 'DENIED_BY_OVERRIDE')
        return 'OVERRIDE_DENY';
    if (outcome === 'NOT_GRANTED')
        return 'NOT_GRANTED';
    return allowedByOverride.has(key) ? 'OVERRIDE_ALLOW' : 'ROLE';
};
/**
 * The single place a permission decision is computed (layer 5).
 *
 * The result is a **conjunction**: the capability *and* the resource ACL. `act:update` without
 * EDITOR on the act is a deny, and EDITOR without `act:update` is a deny too. Every branch below
 * fails closed on purpose:
 *
 * - an unknown key denies, so a typo never opens access;
 * - a resource-scoped key with no level supplied denies, because "not passed" is not "allowed";
 * - ownership does **not** clear the level here. This function compares the level it was handed,
 *   owner or not, and is deliberately naive about who is asking.
 *
 * **Where the owner rule actually lives, and what does not exist yet.** Clearing the level for an
 * owner is a branch of `authorizeResource` in `server/src/domain/access/authorize.util.ts`: it
 * allows when `isOwner` holds and the scope belongs to the `standard` family. That branch is the
 * whole of «owner неотзываем» (`docs/security/permission-model.md`, «Про `isOwner` и ACL»); putting
 * a second copy of it here would be the second ladder this module exists to prevent.
 *
 * A `resolveAcl` answering MANAGER for the owner was named in this docstring for a while and **is
 * not in the tree**. It is future work (EPIC-015), and it is described as future in
 * `server/src/application/iam/use-cases/delete-team.use-case.ts` and
 * `server/src/domain/iam/access/team-access.policy.ts` — a function name in a docstring is worth
 * grepping before trusting. Nothing reads a resource ACL today, because there is no table of grants
 * to read: the comparison below is implemented, the store behind it is not.
 *
 * **Which makes the two sides asymmetric right now, and callers have to know it.** On the server the
 * level is not consulted at all — the route guard asks `authorizeCapability`, and no use-case
 * resolves an `AclScope`, so `authorizeResource` has no callers yet. In the browser this function
 * *is* the live path (`units/iam/service/hooks/use-can.hook.ts`), so a key with a non-null
 * `requiredLevel` asked without a level answers `false` for everybody, the owner included, and the
 * control it draws is drawn for nobody. Ask `canReachSection` for a menu entry, a route guard or a
 * register; ask this function only where the object — and one day its level — is in hand
 * (`docs/security/permission-model.md`, §6).
 */
export const can = (view, key, aclLevel) => {
    if (!isPermissionKey(key))
        return false;
    if (!effectivePermission(view, key))
        return false;
    const need = requiredLevel(key);
    if (need === null)
        return true;
    if (aclLevel === undefined)
        return false;
    return atLeast(aclLevel, need);
};
//# sourceMappingURL=can.util.js.map